/**
 * useMessages — replaces AngularJS MessageService + MessageCtrl message state.
 * Chat messages are received via socket events AND fetched from REST on load.
 */

import { useCallback, useState } from 'react';
import axios from 'axios';
import { BASE_URL } from '@/env';
import type { Message } from '@/types';

interface UseMessagesReturn {
  messages: Message[];
  loading: boolean;
  fetchMessages: () => Promise<void>;
  addMessage: (msg: Message) => void;
}

export function useMessages(): UseMessagesReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchMessages = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const { data } = await axios.get<{ success: boolean; data: Message[] }>(`${BASE_URL}chat`);
      if (data.success) setMessages(data.data);
    } catch {
      // keep existing messages on failure (mirrors original resolve(messages))
    } finally {
      setLoading(false);
    }
  }, []);

  const addMessage = useCallback((msg: Message): void => {
    setMessages(prev => [msg, ...prev]);
  }, []);

  return { messages, loading, fetchMessages, addMessage };
}
